import { useState } from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import { Add as AddIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useActiveRun } from '../../contexts/ActiveRunContext';
import { formatRunTitle } from '../../lib/foam/runLabel';
import StartNewRunDialog from '../foam/StartNewRunDialog';
import { colors } from '../../config/branding';
import { en } from '../../locale';

export default function ActiveRunSelector() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { activeRunId, activeRun, isLoading, isError, refreshRun, setActiveRunId } = useActiveRun();
  const [dialogOpen, setDialogOpen] = useState(false);

  const runName = (() => {
    if (activeRunId === null) return en.runs.noRunSelected;
    if (isLoading) return en.runs.loading;
    if (isError) return en.runs.runUnavailable;
    if (activeRun) return formatRunTitle(activeRun);
    return `Run #${activeRunId}`;
  })();

  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary, fontWeight: 600, letterSpacing: '.02em' }}
      >
        Active run
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={() => (isError ? refreshRun() : navigate('/'))}
        aria-label={en.activeRun.changeAriaLabel}
        sx={{
          mt: 0.75,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          cursor: 'pointer',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          bgcolor: theme.palette.background.default,
          px: 1.5,
          py: 1.1,
          font: 'inherit',
          textAlign: 'left',
          color: theme.palette.text.primary,
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          '&:hover': {
            bgcolor: theme.palette.action.hover,
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: '0.85rem',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: activeRunId === null ? theme.palette.text.secondary : theme.palette.text.primary,
          }}
        >
          {runName}
        </Typography>
        <ChevronRightIcon
          sx={{ fontSize: 18, color: theme.palette.text.secondary, flexShrink: 0 }}
        />
      </Box>

      <Button
        fullWidth
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{
          mt: 1.5,
          textTransform: 'none',
          fontWeight: 600,
          bgcolor: colors.primary,
          boxShadow: 'none',
          '&:hover': { bgcolor: '#1769c4', boxShadow: 'none' },
        }}
      >
        Start a new run
      </Button>

      <StartNewRunDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(run) => {
          setDialogOpen(false);
          setActiveRunId(run.id);
          navigate('/process');
        }}
      />
    </Box>
  );
}
